/**
 * Reads a CSS media query from React.
 *
 * WHY THIS EXISTS, given that the responsive work is deliberately done in CSS: a
 * media query can restyle an element but it cannot change what an element *is*. The
 * navigation sidebar has to be a permanent column on a desktop and an overlay drawer
 * on a phone, and those differ in more than styling — the drawer traps focus, closes
 * on Escape, closes on navigation, is opened by a button that does not exist on
 * desktop, and must be `aria-hidden` from assistive technology while shut. None of
 * that is expressible in a stylesheet.
 *
 * So the rule stays: anything that is only *appearance* belongs in global.css, and
 * this is for the handful of places where a breakpoint changes behaviour. Two callers
 * today (see useNavSidebar), and it should stay a short list.
 *
 * The query string is the source of truth for the breakpoint and has to match the
 * stylesheet's. There is no way to read a CSS breakpoint from JS, so the number is
 * repeated in both — the same trade the token custom properties already make. The
 * constants live in one module so a change is one edit, not a search.
 *
 * `addEventListener('change')` rather than the deprecated `addListener`, with the
 * `matchMedia` existence check because jsdom does not implement it: a test rendering
 * the app shell would otherwise throw on mount, and the honest fallback for "cannot
 * tell" is the desktop layout, which is the one that works without JavaScript.
 */
import { useEffect, useState } from 'react';

/**
 * The width at or below which the sidebar becomes an overlay drawer.
 *
 * 900px, matching the stylesheet's existing sidebar breakpoint — the one that already
 * strips the nav labels. Below it the sidebar was a 76px icon rail permanently eating
 * width from a phone screen; now it leaves the layout and comes back as a drawer.
 */
export const NAV_DRAWER_QUERY = '(max-width: 900px)';

export function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = useState(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return false;
    return window.matchMedia(query).matches;
  });

  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const list = window.matchMedia(query);
    // Set once on subscribe as well as on change: between the initial state and
    // this effect the viewport may already have moved (a rotation during hydration,
    // or a test that resizes before mounting), and a listener alone would not catch
    // a transition that already happened.
    setMatches(list.matches);
    const onChange = (e: MediaQueryListEvent) => setMatches(e.matches);
    list.addEventListener('change', onChange);
    return () => list.removeEventListener('change', onChange);
  }, [query]);

  return matches;
}
